import { EXPERIMENT_UNDERPOWERED_MDE } from "~/ui-rtk/constants/experiments"
import type {
  ExperimentDesignHorizon,
  ExperimentDesignSummary,
  ExperimentRecord,
} from "~/ui-rtk/types/experiments"

export type DesignPowerVerdict = {
  /** The horizon matching the experiment's duration (nearest if absent). */
  horizon: ExperimentDesignHorizon
  /** Minimum detectable lift for that duration, as a fraction. */
  mde: number
  /** The same lift in currency over the test window. */
  detectableLift: number
  currency: string
  powerPct: number
  confidencePct: number
  /** Incremental ROAS the campaign must reach at the planned budget. */
  requiredIroas: number | null
  /** Budget needed at the expected incremental ROAS. */
  minBudget: number | null
  underpowered: boolean
  reasons: string[]
}

export const formatPct = (fraction: number, digits = 1) =>
  `${(fraction * 100).toFixed(digits)}%`

export const formatMoney = (value: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value)
  } catch {
    // Unknown / malformed currency code from the pipeline config.
    return `${currency} ${Math.round(value).toLocaleString("en-US")}`
  }
}

export const findHorizon = (
  summary: ExperimentDesignSummary,
  durationDays: number,
): ExperimentDesignHorizon | null => {
  if (!summary.horizons?.length) {
    return null
  }
  return summary.horizons.reduce((best, horizon) =>
    Math.abs(horizon.days - durationDays) < Math.abs(best.days - durationDays)
      ? horizon
      : best,
  )
}

/**
 * Turn the pipeline's design summary into what the row shows: the MDE for
 * this experiment's duration, its currency equivalent, and — when the user
 * gave a planned budget and/or expected incremental ROAS — whether that
 * budget can produce a detectable lift.
 *
 *   detectable lift $  = MDE × treatment daily revenue × duration
 *   required iROAS     = detectable lift $ / planned budget
 *   minimum budget     = detectable lift $ / expected iROAS
 */
export function describeDesignPower(
  experiment: Pick<
    ExperimentRecord,
    "durationDays" | "totalBudget" | "expectedIroas"
  >,
  summary: ExperimentDesignSummary,
): DesignPowerVerdict | null {
  const horizon = findHorizon(summary, experiment.durationDays)
  if (!horizon || !(summary.treatmentDailyRevenue > 0)) {
    return null
  }

  const currency = summary.currency || "USD"
  // The nearest horizon may not match the experiment's own duration (a
  // pipeline image can drop a horizon). MDE scales roughly with 1/√days, so
  // rescale it to the duration we actually report on instead of pairing a
  // different horizon's MDE with this experiment's day count.
  const mde =
    horizon.days === experiment.durationDays
      ? horizon.mde
      : horizon.mde * Math.sqrt(horizon.days / experiment.durationDays)
  const detectableLift =
    mde * summary.treatmentDailyRevenue * experiment.durationDays

  const budget =
    experiment.totalBudget && experiment.totalBudget > 0
      ? experiment.totalBudget
      : null
  const iroas =
    experiment.expectedIroas && experiment.expectedIroas > 0
      ? experiment.expectedIroas
      : null

  const requiredIroas = budget ? detectableLift / budget : null
  const minBudget = iroas ? detectableLift / iroas : null

  const reasons: string[] = []
  if (mde > EXPERIMENT_UNDERPOWERED_MDE) {
    reasons.push(
      `the smallest detectable lift (${formatPct(mde)}) is above the ${formatPct(EXPERIMENT_UNDERPOWERED_MDE, 0)} guideline`,
    )
  }
  if (budget && minBudget !== null && budget < minBudget) {
    reasons.push(
      `the planned budget (${formatMoney(budget, currency)}) is below the ${formatMoney(minBudget, currency)} needed at ${iroas}x incremental ROAS`,
    )
  }

  return {
    horizon,
    mde,
    detectableLift,
    currency,
    powerPct: summary.powerPct ?? 80,
    confidencePct: summary.confidencePct ?? 90,
    requiredIroas,
    minBudget,
    underpowered: reasons.length > 0,
    reasons,
  }
}
